import React, { useState } from 'react';
import { Row, Col, Button, FormGroup, Label } from 'reactstrap';
import { Field, FieldArray } from 'formik';
import { useTenant } from '../../../../hooks/useTenant';
import { storage } from '../../../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { ACHIEVEMENT_ICONS, getAttendanceImage } from '../../../../models/gamification';

const GamificationAttendanceMilestones = ({ values, setFieldValue }) => {
    const [editingIndex, setEditingIndex] = useState(null);
    const [isUploading, setIsUploading] = useState({});
    const { idTenant } = useTenant();

    const handleImageUpload = async (e, globalIndex, achivId) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, selecione uma imagem válida.');
            return;
        }

        if (file.size > 2 * 1024 * 1024) { // 2MB limit
            toast.error('A imagem deve ter no máximo 2MB.');
            return;
        }

        setIsUploading(prev => ({ ...prev, [globalIndex]: true }));

        try {
            const fileExtension = file.name.split('.').pop();
            const fileName = `${achivId}_${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, `tenants/${idTenant}/gamification/achievements/${fileName}`);

            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            setFieldValue(`achievements.${globalIndex}.imageId`, downloadURL);
            toast.success('Imagem atualizada com sucesso!');
        } catch (error) {
            console.error("Erro ao fazer upload:", error);
            toast.error('Erro ao fazer upload da imagem.');
        } finally {
            setIsUploading(prev => ({ ...prev, [globalIndex]: false }));
        }
    };

    return (
        <div className="mt-5 mb-4 border-top pt-4">
            <h5 className="fw-bold mb-1 d-flex align-items-center">
                <i className="mdi mdi-medal-outline text-warning me-2 font-size-22"></i>
                Métricas de Conquista por Frequência
            </h5>
            <p className="text-muted mb-4 font-size-13">Recompensas recebidas pelos alunos ao atingirem marcos de presença.</p>

            <FieldArray name="achievements">
                {({ push, remove }) => {
                    const attendanceIndices = values.achievements
                        .map((achiv, index) => ({ achiv, index }))
                        .filter(item => item.achiv.type === 'attendance');

                    // Sort by threshold visually
                    attendanceIndices.sort((a, b) => a.achiv.threshold - b.achiv.threshold);

                    return (
                        <>
                            <Row className="g-3">
                                {attendanceIndices.map(({ achiv, index }) => (
                                    <Col lg={3} md={4} sm={6} key={`achiv_${index}`}>
                                        {editingIndex === index ? (
                                            <div className="p-3 bg-white rounded-0 border border-warning text-start h-100 position-relative">
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="rounded-0 position-absolute"
                                                    style={{ top: '-10px', right: '-10px', width: '28px', height: '28px', padding: 0 }}
                                                    onClick={() => remove(index)}
                                                >
                                                    <i className="mdi mdi-close"></i>
                                                </Button>

                                                <FormGroup className="mb-2">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">Nome</Label>
                                                    <Field type="text" className="form-control form-control-sm bg-light border-0 rounded-0" name={`achievements.${index}.name`} />
                                                </FormGroup>

                                                <FormGroup className="mb-2">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">Meta (Aulas)</Label>
                                                    <Field type="number" className="form-control form-control-sm bg-light border-0 rounded-0" name={`achievements.${index}.threshold`} />
                                                </FormGroup>

                                                <FormGroup className="mb-2">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">XP Ganho</Label>
                                                    <Field type="number" className="form-control form-control-sm bg-light border-0 rounded-0" name={`achievements.${index}.rewardXp`} />
                                                </FormGroup>

                                                <FormGroup className="mb-3">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">Upload de Imagem</Label>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        className="form-control form-control-sm bg-light border-0 rounded-0"
                                                        disabled={isUploading[index]}
                                                        onChange={(e) => handleImageUpload(e, index, achiv.id || `achiv_attendance_${index}`)}
                                                    />
                                                    {isUploading[index] && (
                                                        <small className="text-primary d-block mt-1">
                                                            <i className="mdi mdi-loading mdi-spin me-1"></i>Enviando imagem...
                                                        </small>
                                                    )}
                                                </FormGroup>

                                                <FormGroup className="mb-3">
                                                    <Label className="font-size-11 text-muted text-uppercase fw-bold mb-1">Ícone</Label>
                                                    <Field as="select" className="form-select form-select-sm bg-light border-0 rounded-0" name={`achievements.${index}.icon`}>
                                                        {ACHIEVEMENT_ICONS.map(icon => <option key={icon} value={icon}>{icon.replace('mdi-', '')}</option>)}
                                                    </Field>
                                                    <small className="text-muted font-size-10 mt-1 d-block">Ícone padrão caso não tenha imagem.</small>
                                                </FormGroup>

                                                <Button color="primary" size="sm" className="w-100 rounded-0" onClick={() => setEditingIndex(null)}>
                                                    OK
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="p-3 bg-white rounded-0 border text-center h-100 position-relative" style={{ transition: 'all 0.3s ease' }}>
                                                <Button
                                                    color="light"
                                                    size="sm"
                                                    className="rounded-0 position-absolute border text-secondary"
                                                    style={{ top: '5px', right: '5px', padding: '2px 6px', fontSize: '12px' }}
                                                    onClick={() => setEditingIndex(index)}
                                                >
                                                    <i className="mdi mdi-pencil"></i>
                                                </Button>

                                                <div className="position-absolute top-0 start-50 translate-middle badge bg-primary rounded-0 px-2 py-1 font-size-11">
                                                    {achiv.threshold} Aulas
                                                </div>
                                                <div className="mt-3 mb-2">
                                                    <div className="mx-auto d-flex align-items-center justify-content-center mb-2" style={{ width: '72px', height: '72px', overflow: 'hidden' }}>
                                                        {achiv.imageId ? (
                                                            <img src={getAttendanceImage(achiv.imageId)} alt={achiv.name} style={{ width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' }} />
                                                        ) : (
                                                            <i className={`mdi ${achiv.icon} text-warning font-size-48`}></i>
                                                        )}
                                                    </div>
                                                </div>
                                                <h6 className="fw-bold text-dark mb-1 font-size-13">{achiv.name}</h6>
                                                <div className="text-success fw-bold font-size-14 d-flex align-items-center justify-content-center mt-2">
                                                    <i className="mdi mdi-star-circle me-1"></i>
                                                    +{achiv.rewardXp} XP
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                ))}

                                {attendanceIndices.length === 0 && (
                                    <Col md={12}>
                                        <div className="text-center p-4 rounded-0 border border-dashed border-secondary mb-2">
                                            <p className="text-muted small mb-0">Nenhuma métrica de frequência configurada.</p>
                                        </div>
                                    </Col>
                                )}
                            </Row>

                            <div className="mt-4 text-center">
                                <Button
                                    type="button"
                                    color="primary"
                                    outline
                                    className="rounded-0 px-4 fw-bold"
                                    onClick={() => {
                                        push({
                                            id: `achiv_attendance_${Date.now()}`,
                                            name: 'Nova Conquista de Frequência',
                                            description: 'Recompensa por aulas assistidas',
                                            type: 'attendance',
                                            threshold: 10,
                                            icon: 'mdi-trophy',
                                            rewardXp: 10
                                        });
                                        // Set editing index to the newly added item (which is at the end of the array)
                                        setEditingIndex(values.achievements.length);
                                    }}
                                >
                                    <i className="mdi mdi-plus me-2"></i>
                                    Adicionar Nova Métrica
                                </Button>
                            </div>
                        </>
                    );
                }}
            </FieldArray>
        </div>
    );
};

export default GamificationAttendanceMilestones;
